import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { formatMoney } from '../lib/format';
import type { BillingStatus, Plan, PlanInfo, Transaction } from '../types';

interface BillingPanelProps {
  billing: BillingStatus;
  fetchPlans: () => Promise<PlanInfo[]>;
  fetchTransactions: () => Promise<Transaction[]>;
  checkout: (plan: Plan) => Promise<string>;
  openPortal: () => Promise<string>;
  cancel: () => Promise<BillingStatus>;
  resume: () => Promise<BillingStatus>;
  onBillingChange: (billing: BillingStatus) => void;
  push: (kind: 'ok' | 'error' | 'info' | 'warn', message: string) => void;
}

function formatPrice(info: PlanInfo | undefined): string {
  if (!info) return '';
  return `${formatMoney(info.amount, info.currency)}/${info.interval}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

const STATUS_LABEL: Record<string, string> = {
  trialing: 'Free trial',
  active: 'Active',
  past_due: 'Payment failed',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
};

function statusBadge(t: Transaction): { tone: string; label: string } {
  if (t.refunded) return { tone: 'warn', label: 'refunded' };
  if (t.status === 'succeeded') return { tone: 'ok', label: 'succeeded' };
  if (t.status === 'failed') return { tone: 'danger', label: 'failed' };
  return { tone: '', label: t.status };
}

/** The Billing tab: current plan/status, billing history, and either "subscribe" or "manage billing"/"cancel", depending on where the account stands. */
export function BillingPanel({
  billing,
  fetchPlans,
  fetchTransactions,
  checkout,
  openPortal,
  cancel,
  resume,
  onBillingChange,
  push,
}: BillingPanelProps) {
  const [plans, setPlans] = useState<PlanInfo[] | null>(null);
  const [transactions, setTransactions] = useState<Transaction[] | null>(null);
  const [busy, setBusy] = useState<'monthly' | 'annual' | 'portal' | 'cancel' | 'resume' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPlans()
      .then(setPlans)
      .catch(() => setPlans([]));
  }, [fetchPlans]);

  useEffect(() => {
    fetchTransactions()
      .then(setTransactions)
      .catch(() => setTransactions([]));
  }, [fetchTransactions]);

  const monthly = plans?.find((p) => p.plan === 'monthly');
  const annual = plans?.find((p) => p.plan === 'annual');
  const canManage = billing.status === 'active' || billing.status === 'past_due' || billing.status === 'canceled';
  const canCancel = (billing.status === 'active' || billing.status === 'past_due') && !billing.cancelAtPeriodEnd;

  async function subscribe(plan: Plan) {
    setBusy(plan);
    setError(null);
    try {
      window.location.href = await checkout(plan);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      setBusy(null);
    }
  }

  async function manage() {
    setBusy('portal');
    setError(null);
    try {
      window.location.href = await openPortal();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      setBusy(null);
    }
  }

  async function doCancel() {
    if (
      !window.confirm(
        billing.currentPeriodEnd
          ? `Cancel your subscription? You'll keep access until ${formatDate(billing.currentPeriodEnd)}, then it won't renew.`
          : "Cancel your subscription? It won't renew at the end of the current period."
      )
    ) {
      return;
    }
    setBusy('cancel');
    setError(null);
    try {
      const updated = await cancel();
      onBillingChange(updated);
      push('ok', 'Subscription canceled. You keep access until the end of the current period.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(null);
    }
  }

  async function doResume() {
    setBusy('resume');
    setError(null);
    try {
      const updated = await resume();
      onBillingChange(updated);
      push('ok', 'Cancellation undone — your subscription will renew as normal.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card">
      <div className="card-body billing-panel">
        <div className="billing-status-row">
          <div>
            <div className={`billing-status-badge status-${billing.status ?? 'none'}`}>
              {STATUS_LABEL[billing.status ?? ''] ?? 'No plan'}
            </div>
            {billing.status === 'trialing' && billing.daysLeftInTrial !== null && (
              <p className="billing-detail">
                {billing.daysLeftInTrial > 0
                  ? `${billing.daysLeftInTrial} day${billing.daysLeftInTrial === 1 ? '' : 's'} left in your trial.`
                  : 'Your trial ends today.'}
              </p>
            )}
            {billing.status === 'active' && billing.plan && (
              <p className="billing-detail">
                {billing.plan === 'monthly' ? 'Monthly plan' : 'Annual plan'}
                {billing.currentPeriodEnd &&
                  (billing.cancelAtPeriodEnd
                    ? ` — cancels on ${formatDate(billing.currentPeriodEnd)}.`
                    : ` — renews ${formatDate(billing.currentPeriodEnd)}.`)}
              </p>
            )}
            {billing.status === 'past_due' && (
              <p className="billing-detail">
                Your last payment failed. Update your payment method to avoid losing access.
              </p>
            )}
            {billing.status === 'canceled' && <p className="billing-detail">Subscribe again to keep using Z3NTRAFY.</p>}
            {billing.cancelAtPeriodEnd && billing.status !== 'canceled' && (
              <p className="billing-detail warn">
                Your subscription is set to cancel{billing.currentPeriodEnd ? ` on ${formatDate(billing.currentPeriodEnd)}` : ''}.
              </p>
            )}
          </div>
          <div className="billing-actions">
            {canManage && (
              <button className="btn" onClick={manage} disabled={busy !== null}>
                {busy === 'portal' ? <span className="spin" /> : null} Manage billing
              </button>
            )}
            {canCancel && (
              <button className="btn ghost" onClick={doCancel} disabled={busy !== null}>
                {busy === 'cancel' ? <span className="spin" /> : null} Cancel subscription
              </button>
            )}
            {billing.cancelAtPeriodEnd && billing.status !== 'canceled' && (
              <button className="btn" onClick={doResume} disabled={busy !== null}>
                {busy === 'resume' ? <span className="spin" /> : null} Resume subscription
              </button>
            )}
          </div>
        </div>

        {error && <div className="auth-error">{error}</div>}

        {billing.status !== 'active' && (
          <>
            <h3 className="billing-plans-heading">
              {billing.status === 'trialing' ? 'Subscribe any time — no need to wait for your trial to end' : 'Choose a plan'}
            </h3>
            <div className="plan-grid">
              <button className="plan-card" onClick={() => subscribe('monthly')} disabled={busy !== null || plans === null}>
                <div className="plan-name">Monthly</div>
                <div className="plan-price">{plans === null ? <span className="spin" /> : formatPrice(monthly)}</div>
                <div className="plan-cta">{busy === 'monthly' ? <span className="spin" /> : 'Subscribe'}</div>
              </button>
              <button
                className="plan-card featured"
                onClick={() => subscribe('annual')}
                disabled={busy !== null || plans === null}
              >
                <div className="plan-badge">Best value</div>
                <div className="plan-name">Annual</div>
                <div className="plan-price">{plans === null ? <span className="spin" /> : formatPrice(annual)}</div>
                <div className="plan-cta">{busy === 'annual' ? <span className="spin" /> : 'Subscribe'}</div>
              </button>
            </div>
          </>
        )}

        <h3 className="billing-plans-heading">Billing history</h3>
        {transactions === null ? (
          <div className="empty">Loading…</div>
        ) : transactions.length === 0 ? (
          <div className="empty">No charges yet — nothing's been billed to this account.</div>
        ) : (
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => {
                  const badge = statusBadge(t);
                  return (
                    <tr key={t.id}>
                      <td>{new Date(t.created).toLocaleDateString()}</td>
                      <td>
                        {t.description ?? '—'}
                        {t.receiptUrl && (
                          <>
                            {' '}
                            <a href={t.receiptUrl} target="_blank" rel="noreferrer">
                              receipt
                            </a>
                          </>
                        )}
                      </td>
                      <td>
                        {formatMoney(t.amount, t.currency)}
                        {t.amountRefunded > 0 && (
                          <div className="help">{formatMoney(t.amountRefunded, t.currency)} refunded</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${badge.tone}`}>{badge.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
